import { useCallback, useState } from "react";
import { useDispatch } from "react-redux";
import { showError } from "../../../../redux/common/actions";
import {
	bulkAssignModelTaskRelation,
	getModelTaskRelationSummary,
} from "services/models/modelDetails/modelTasks";

// One entry per bulk-assign endpoint. `caption` reads the site's custom
// captions, so a customer calling Zones something else still sees their word.
const RELATIONS = [
	{ key: "roles", caption: (c) => c?.rolePlural || "Roles" },
	{ key: "intervals", caption: (c) => c?.intervalPlural || "Intervals" },
	{ key: "stages", caption: (c) => c?.stagePlural || "Stages" },
	{ key: "zones", caption: (c) => c?.zonePlural || "Zones" },
	{ key: "arrangements", caption: (c) => c?.arrangementPlural || "Arrangements" },
];

/**
 * Drives the bulk assign dialog. The summary endpoint returns every option for
 * the relation together with how many of the selected tasks already have it,
 * which is what lets the dialog show one pre-ticked checkbox list rather than
 * separate add and remove columns.
 */
const useBulkTaskAssign = ({ modelVersionId, onAssigned }) => {
	const dispatch = useDispatch();
	const [relation, setRelation] = useState(null);
	const [summary, setSummary] = useState({ taskCount: 0, options: [] });
	const [isLoading, setIsLoading] = useState(false);
	const [isSaving, setIsSaving] = useState(false);

	const open = useCallback(
		async (relationKey, taskIds) => {
			setRelation(RELATIONS.find((r) => r.key === relationKey));
			setSummary({ taskCount: 0, options: [] });
			setIsLoading(true);
			try {
				const response = await getModelTaskRelationSummary({
					relation: relationKey,
					modelVersionId,
					taskIds,
				});
				if (!response?.status) {
					dispatch(showError(response?.data?.title || "Could not load options"));
					setRelation(null);
					return;
				}
				setSummary({
					taskCount: response.data?.taskCount ?? 0,
					options: response.data?.options ?? [],
				});
			} finally {
				setIsLoading(false);
			}
		},
		[dispatch, modelVersionId]
	);

	const close = useCallback(() => setRelation(null), []);

	const apply = useCallback(
		async ({ taskIds, addIds, removeIds }) => {
			setIsSaving(true);
			try {
				const response = await bulkAssignModelTaskRelation({
					relation: relation.key,
					modelVersionId,
					taskIds,
					addIds,
					removeIds,
				});
				if (!response?.status) {
					dispatch(
						showError(response?.data?.title || "Could not update the selected tasks")
					);
					return false;
				}
				await onAssigned();
				setRelation(null);
				return true;
			} finally {
				setIsSaving(false);
			}
		},
		[dispatch, modelVersionId, onAssigned, relation]
	);

	return { RELATIONS, relation, summary, isLoading, isSaving, open, close, apply };
};

export { RELATIONS };
export default useBulkTaskAssign;
